import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';

/**
 * One-time cleanup for devices that still hold the obsolete root-scoped
 * service worker (an early release registered '/ngsw-worker.js' with scope '/'
 * instead of '/landing/ngsw-worker.js' with scope '/landing/').
 * Targets only that exact scope + script URL, so the correct landing worker
 * and any other registrations on the origin are left untouched.
 */
function cleanupObsoleteRootServiceWorker(): void {
	const cleanupFlag = 'fpl_root_sw_cleaned_v1';
	try {
		if (!('serviceWorker' in navigator) || localStorage.getItem(cleanupFlag) === 'true') {
			return;
		}
	} catch {
		return;
	}

	navigator.serviceWorker.getRegistrations()
		.then((registrations) => {
			const staleRegistrations = registrations.filter((reg) => {
				const scriptURL = (reg.active ?? reg.waiting ?? reg.installing)?.scriptURL ?? '';
				return reg.scope === `${location.origin}/` && scriptURL === `${location.origin}/ngsw-worker.js`;
			});
			return Promise.all(staleRegistrations.map((reg) => reg.unregister()))
				.then((results) => results.some((didUnregister) => didUnregister === true));
		})
		.then(async (didCleanup) => {
			try {
				localStorage.setItem(cleanupFlag, 'true');
			} catch { /* storage unavailable; cleanup is idempotent anyway */ }

			if (didCleanup) {
				// The Angular SW names its caches 'ngsw:<scope>:...', so 'ngsw:/:'
				// only matches caches of the obsolete root-scoped worker.
				try {
					const cacheNames = await caches.keys();
					await Promise.all(cacheNames.filter((name) => name.startsWith('ngsw:/:')).map((name) => caches.delete(name)));
				} catch { /* ignore */ }
				// Reload once so the current page stops being controlled by the stale worker.
				location.reload();
			}
		})
		.catch(() => { /* never block app startup on cleanup */ });
}

cleanupObsoleteRootServiceWorker();

bootstrapApplication(AppComponent, appConfig)
  .catch((err) => console.error(err));
